import React from 'react';
import axios from '../../../helpers/axios';
import config from '../../../helpers/config';
import ConversationModel from 'models/ConversationModel';
import Price from 'components/common/Price';
import FormComponentDemo from '../demo/FormComponentDemo';
import Toggle from 'components/common/Toggle';
import { displayWarningModal } from 'helpers/services/warningModalService';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';
import HoverTooltip from 'components/common/HoverTooltip';

interface Props {}

interface Trade {}

interface State {
    exampleToggle: boolean;
}

class DevPage2 extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            exampleToggle: false,
        };
    }

    componentDidMount() {}

    USPhoneNumberDisplay = (phoneNumber: any) => {
        // Function to format the phone number
        const formatPhoneNumber = (num: string) => {
            if (!num) {
                return '';
            }

            // Remove all non-digit characters
            const cleaned = ('' + num).replace(/\D/g, '');

            // Check if it's a 10-digit number (standard US format)
            if (cleaned.length === 10) {
                // Format as (XXX) XXX-XXXX
                return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6, 10)}`;
            }
            // Handle 11-digit numbers (with leading 1, often from international dialing)
            else if (cleaned.length === 11 && cleaned.startsWith('1')) {
                // Format as +1 (XXX) XXX-XXXX
                return `+1 (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7, 11)}`;
            }
            // If not 10 or 11 digits, or not starting with 1 for 11 digits, return as is (or handle as an error)
            // For this example, we'll just return the cleaned number if it doesn't fit the US pattern.
            return cleaned;
        };

        const formattedNumber = formatPhoneNumber(phoneNumber);

        if (!formattedNumber) {
            return <span>N/A</span>; // Or some other placeholder
        }

        return (
            <a href={`tel:${formattedNumber.replace(/\D/g, '')}`} aria-label={`Call ${formattedNumber}`}>
                {formattedNumber}
            </a>
        );
    };

    render() {
        return (
            <div className="lis-page text-black p-6 max-w-6xl mx-auto pb-48">
                <FormComponentDemo />
                <div className="bg-white rounded-lg shadow-md p-6 mb-8 flex flex-col space-y-4">
                    <div>
                        <Toggle
                            name="exampleToggle"
                            label="Example Toggle"
                            value={this.state.exampleToggle}
                            onChange={(newValue) => this.setState({ exampleToggle: newValue })}
                        />
                    </div>
                    <div>
                        <Toggle
                            name="exampleToggle"
                            label="Example Toggle"
                            value={this.state.exampleToggle}
                            onChange={(newValue) => this.setState({ exampleToggle: newValue })}
                            isSmall={true}
                        />
                    </div>
                    <button
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
                        onClick={() => {
                            displayWarningModal('This is a warning message. Are you sure you want to proceed?').then(
                                (result) => {
                                    if (result) {
                                        console.log('User confirmed the action.');
                                    } else {
                                        console.log('User canceled the action.');
                                    }
                                },
                            );
                        }}
                    >
                        Click to show warning modal
                    </button>
                    <button
                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
                        onClick={() => {
                            displayConfirmationModal(`Are you sure you want to delete this item?`)
                                .then(() => {
                                    console.log('User confirmed deletion.');
                                })
                                .catch((_) => console.log('User canceled deletion.'));
                        }}
                    >
                        Click to show delete confirmation modal
                    </button>
                    <div>
                        <h2>Hover tooltip:</h2>
                        <HoverTooltip content="This is a hover tooltip example.">
                            <span className="text-blue-600 cursor-pointer">Hover over this text</span>
                        </HoverTooltip>
                    </div>
                    <div>
                        <h2>Hover tooltip with customized content:</h2>
                        <HoverTooltip
                            content={
                                <div className="p-4 bg-white border border-blue-600 text-blue-600 rounded shadow">
                                    <h3 className="text-lg font-semibold">Custom Tooltip Content</h3>
                                    <p>This tooltip can contain any React component or HTML.</p>
                                    <Price price={12345} />
                                </div>
                            }
                        >
                            <span className="text-blue-600 cursor-pointer">Hover over this text</span>
                        </HoverTooltip>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6 mb-8 flex flex-col space-y-4 h-96">
                    <h2>Example of flex grow</h2>
                    <div className="border border-blue-400 rounded-lg">1</div>
                    <div className="border border-blue-400 rounded-lg grow">2</div>
                    <div className="border border-blue-400 rounded-lg">3</div>
                    <div className="border border-blue-400 rounded-lg">4</div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 mb-8 flex flex-col space-y-4 h-">
                    <h1>US Phone Number Examples</h1>

                    <h2>Valid Numbers:</h2>
                    <p>
                        <strong>10 Digits:</strong> {this.USPhoneNumberDisplay('1234567890')}
                    </p>
                    <p>
                        <strong>10 Digits (with some formatting):</strong> {this.USPhoneNumberDisplay('(987) 654-3210')}
                    </p>
                    <p>
                        <strong>11 Digits (with +1):</strong> {this.USPhoneNumberDisplay('+1-555-123-4567')}
                    </p>
                    <p>
                        <strong>11 Digits (with 1):</strong> {this.USPhoneNumberDisplay('12223334444')}
                    </p>

                    <h2>Invalid/Edge Cases:</h2>
                    <p>
                        <strong>Short Number:</strong> {this.USPhoneNumberDisplay('123')}
                    </p>
                    <p>
                        <strong>Long Number:</strong> {this.USPhoneNumberDisplay('1234567890123')}
                    </p>
                    <p>
                        <strong>Non-numeric:</strong> {this.USPhoneNumberDisplay('abc-def-ghij')}
                    </p>
                    <p>
                        <strong>Empty String:</strong> {this.USPhoneNumberDisplay('')}
                    </p>
                    <p>
                        <strong>Null:</strong> {this.USPhoneNumberDisplay(null)}
                    </p>
                    <p>
                        <strong>Undefined:</strong> {this.USPhoneNumberDisplay(undefined)}
                    </p>
                </div>
            </div>
        );
    }
}

export default DevPage2;
